"use client";

import * as React from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import type { CellValidationResult } from "@quovy/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../primitives/tooltip";
import { cn } from "../../../utils";
import { useLazyOverlay } from "./use-lazy-overlay";

export interface ValidationIndicatorProps {
  results: CellValidationResult[] | undefined;
  className?: string;
}

/**
 * Renders a warn or fail icon based on the highest-severity validation
 * result. Tooltip enumerates each rule message. Returns `null` when there
 * are no validation results.
 */
export function ValidationIndicator({ results, className }: ValidationIndicatorProps) {
  if (!results || results.length === 0) return null;

  const hasFail = results.some((r) => r.severity === "fail");
  const Icon = hasFail ? ShieldAlert : AlertTriangle;
  const color = hasFail ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400";
  const label = hasFail ? "Validation failed" : "Validation warning";
  const { primed, triggerProps } = useLazyOverlay();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          {...triggerProps}
          className={cn("inline-flex shrink-0 items-center", color, className)}
          aria-label={label}
        >
          <Icon className="size-3.5" aria-hidden="true" />
        </span>
      </TooltipTrigger>
      {primed && (
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <div className="text-xs font-medium">{label}</div>
            <ul className="space-y-0.5">
              {results.map((r) => (
                <li
                  key={r.ruleId}
                  className={cn(
                    "text-[11px]",
                    r.severity === "fail" ? "text-red-300" : "text-amber-300",
                  )}
                >
                  {r.message}
                </li>
              ))}
            </ul>
          </div>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
